/**
 * 霓虹蛇境 (Neon Snake Realm) - 粒子系统工具模块
 *
 * 本模块提供一组纯函数，用于管理游戏中的粒子特效系统。
 * 所有函数均为无副作用的纯函数，输入参数明确，返回值确定。
 * 函数不依赖任何外部可变状态，仅基于传入的参数进行计算。
 *
 * 粒子系统涵盖以下功能：
 * - 爆炸粒子生成（食物收集、道具拾取）
 * - 粒子生命周期更新与淘汰
 * - 粒子数量限制（桌面端/移动端自适应）
 * - 游戏结束蛇身碎裂粒子
 * - 蛇身渐变色映射
 */

import type { Particle, Position } from '@/types/game';
import { GAME_CONFIG, COLOR_SCHEME, SCORE_THRESHOLDS } from '@/game/constants';

// ============================================================================
// 内部工具函数
// ============================================================================

/**
 * 生成指定范围内的随机整数 [min, max]（两端均包含）。
 *
 * 使用 Math.random() 作为随机源，适用于游戏粒子系统的视觉随机性需求。
 * 不适用于密码学安全场景。
 *
 * @param min - 范围最小值（包含）
 * @param max - 范围最大值（包含）
 * @returns 在 [min, max] 范围内的随机整数
 */
function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * 从只读数组中随机选取一个元素。
 *
 * 使用 Math.random() 作为随机源。
 * 如果数组为空，返回 undefined（调用方应确保数组非空）。
 *
 * @param arr - 只读数组
 * @returns 随机选取的数组元素
 */
function randomPick<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

// ============================================================================
// 粒子生成
// ============================================================================

/**
 * 在指定位置生成爆炸粒子数组。
 *
 * 每个粒子具有以下随机属性：
 * - **速度方向**：均匀分布在 0 到 2π 弧度之间
 * - **速度大小**：在 GAME_CONFIG.particleSpeedRange 范围内随机（转换为像素/毫秒）
 * - **生命周期**：在 GAME_CONFIG.particleLifeRange 范围内随机（毫秒）
 * - **颜色**：使用传入的 color 参数（所有粒子统一颜色）
 * - **尺寸**：在 2 到 5 像素之间随机
 *
 * 粒子数量受以下限制：
 * - 实际生成数量 = min(count, maxParticles)，其中 maxParticles 取决于设备类型
 * - 桌面端上限：GAME_CONFIG.maxParticlesDesktop
 * - 移动端上限：GAME_CONFIG.maxParticlesMobile
 *
 * @param x - 爆炸中心 X 坐标（网格坐标，渲染时需转换为像素坐标）
 * @param y - 爆炸中心 Y 坐标（网格坐标，渲染时需转换为像素坐标）
 * @param color - 粒子颜色（CSS 颜色字符串）
 * @param count - 期望生成的粒子数量
 * @param isMobile - 是否为移动端设备
 * @returns 新生成的粒子数组
 *
 * @example
 * const particles = spawnParticles(10, 5, '#ff3366', 20, false);
 * // 生成最多 20 个品红色粒子，位置在网格坐标 (10, 5)
 */
export function spawnParticles(
  x: number,
  y: number,
  color: string,
  count: number,
  isMobile: boolean
): Particle[] {
  // 根据设备类型确定粒子数量上限
  const maxParticles = isMobile
    ? GAME_CONFIG.maxParticlesMobile
    : GAME_CONFIG.maxParticlesDesktop;

  // 实际生成数量不超过上限
  const actualCount = Math.min(count, maxParticles);

  // 预分配数组以提高性能
  const particles: Particle[] = new Array(actualCount);

  // 解构常量以缩短属性访问链
  const [speedMin, speedMax] = GAME_CONFIG.particleSpeedRange;
  const [lifeMin, lifeMax] = GAME_CONFIG.particleLifeRange;

  for (let i = 0; i < actualCount; i++) {
    // 随机角度（弧度），均匀分布在 [0, 2π)
    const angle = Math.random() * Math.PI * 2;

    // 随机速度大小，转换为像素/毫秒（原始单位为像素/秒）
    const speed = randomInt(speedMin, speedMax) / 1000;

    // 随机生命周期（毫秒）
    const life = randomInt(lifeMin, lifeMax);

    particles[i] = {
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life,
      maxLife: life,
      color,
      size: randomInt(2, 5),
    };
  }

  return particles;
}

// ============================================================================
// 粒子更新
// ============================================================================

/**
 * 更新粒子数组：移动粒子位置、减少生命周期、移除死亡粒子。
 *
 * 对每个粒子执行以下操作：
 * 1. 根据速度和时间增量更新位置：`newPosition = oldPosition + velocity * deltaTime`
 * 2. 减少剩余生命周期：`newLife = oldLife - deltaTime`
 * 3. 过滤掉生命周期已耗尽（life <= 0）的粒子
 *
 * 此函数不会修改原始数组，而是创建并返回一个新的粒子数组。
 * 使用 filter 链式调用确保不可变性。
 *
 * 性能说明：
 * - 时间复杂度 O(n)，其中 n 为输入粒子数量
 * - 空间复杂度 O(n)，创建新数组
 * - 对于典型粒子数量（< 200），性能影响可忽略
 *
 * @param particles - 当前活跃的粒子数组
 * @param deltaTime - 自上一帧以来的时间增量（毫秒）
 * @returns 更新后的粒子数组（已移除死亡粒子）
 *
 * @example
 * const updated = updateParticles(particles, 16.67); // 约 60 FPS 的一帧时间
 */
export function updateParticles(
  particles: Particle[],
  deltaTime: number
): Particle[] {
  // 如果粒子数组为空，直接返回空数组，避免不必要的计算
  if (particles.length === 0) {
    return particles;
  }

  // 使用 map 更新每个粒子的状态，然后 filter 移除死亡粒子
  // 两步操作可合并为 reduce 以提升性能，但当前写法更清晰
  return particles
    .map((p) => ({
      ...p,
      x: p.x + p.vx * deltaTime,
      y: p.y + p.vy * deltaTime,
      life: p.life - deltaTime,
    }))
    .filter((p) => p.life > 0);
}

// ============================================================================
// 粒子数量限制
// ============================================================================

/**
 * 将粒子数组裁剪到设备类型对应的最大数量限制。
 *
 * 如果粒子数量超过上限，保留最新的粒子（数组末尾），丢弃最旧的粒子（数组开头）。
 * 这是因为新生成的粒子通常追加到数组末尾，保留末尾可确保最新特效不被裁剪。
 *
 * 如果粒子数量未超过上限，原样返回数组。
 *
 * @param particles - 当前活跃的粒子数组
 * @param isMobile - 是否为移动端设备
 * @returns 裁剪后的粒子数组
 *
 * @example
 * const limited = limitParticles(particles, true);
 * // 移动端：最多保留 GAME_CONFIG.maxParticlesMobile 个粒子
 */
export function limitParticles(
  particles: Particle[],
  isMobile: boolean
): Particle[] {
  const maxParticles = isMobile
    ? GAME_CONFIG.maxParticlesMobile
    : GAME_CONFIG.maxParticlesDesktop;

  // 如果粒子数量未超过上限，直接返回原数组
  if (particles.length <= maxParticles) {
    return particles;
  }

  // 保留最新的粒子（数组末尾），丢弃最旧的粒子（数组开头）
  // 使用负索引 slice 确保获取最后 maxParticles 个元素
  return particles.slice(-maxParticles);
}

// ============================================================================
// 蛇身渐变色映射
// ============================================================================

/**
 * 根据当前得分获取蛇身渐变色数组。
 *
 * 渐变阶段划分（基于 SCORE_THRESHOLDS）：
 * - **初期**（0 ~ EARLY_MAX - 1 分）：青绿到天蓝渐变
 * - **中期**（EARLY_MAX ~ MID_MAX - 1 分）：紫蓝到粉紫渐变
 * - **后期**（MID_MAX 分及以上）：橙到金渐变
 *
 * 返回的颜色数组包含两个颜色值，用于在蛇身渲染时进行线性插值。
 * 返回的是只读引用，调用方不应修改返回的数组。
 *
 * @param score - 当前游戏得分
 * @returns 包含两个 CSS 颜色字符串的只读数组 [头部颜色, 尾部颜色]
 *
 * @example
 * getSnakeGradientColors(0)   // => ['#00ff88', '#00ccff']（初期青绿）
 * getSnakeGradientColors(60)  // => ['#6600ff', '#ff00cc']（中期紫蓝）
 * getSnakeGradientColors(200) // => ['#ff6600', '#ffcc00']（后期金橙）
 */
export function getSnakeGradientColors(score: number): readonly string[] {
  if (score < SCORE_THRESHOLDS.EARLY_MAX) {
    return COLOR_SCHEME.snakeGradientEarly;
  }
  if (score < SCORE_THRESHOLDS.MID_MAX) {
    return COLOR_SCHEME.snakeGradientMid;
  }
  return COLOR_SCHEME.snakeGradientLate;
}

// ============================================================================
// 游戏结束粒子生成
// ============================================================================

/**
 * 在游戏结束时为蛇身的每个节段生成碎裂粒子。
 *
 * 对蛇身的每个节段：
 * 1. 从当前得分对应的渐变色中随机选取一个颜色
 * 2. 生成 3 到 8 个粒子（随机数量）
 * 3. 粒子以该节段的网格坐标为中心爆炸
 *
 * 生成的粒子总数受设备类型对应的最大粒子数限制。
 * 如果蛇身较长导致粒子数量超过上限，将裁剪到上限。
 *
 * 此函数组合使用 spawnParticles 和 limitParticles，
 * 确保生成的粒子符合系统限制。
 *
 * @param snakeBody - 蛇身坐标数组（索引 0 为头部）
 * @param score - 当前游戏得分（用于确定渐变色系）
 * @param isMobile - 是否为移动端设备
 * @returns 游戏结束碎裂粒子数组
 *
 * @example
 * const body = [{ x: 10, y: 5 }, { x: 9, y: 5 }, { x: 8, y: 5 }];
 * const deathParticles = spawnDeathParticles(body, 120, false);
 * // 为 3 个节段各生成 3-8 个粒子，颜色来自中期紫蓝色系
 */
export function spawnDeathParticles(
  snakeBody: Position[],
  score: number,
  isMobile: boolean
): Particle[] {
  // 获取当前得分对应的渐变色系
  const gradientColors = getSnakeGradientColors(score);

  // 预计算最大粒子数以优化性能
  const maxParticles = isMobile
    ? GAME_CONFIG.maxParticlesMobile
    : GAME_CONFIG.maxParticlesDesktop;

  const allParticles: Particle[] = [];

  // 遍历蛇身的每个节段，为每个节段生成碎裂粒子
  for (const segment of snakeBody) {
    // 从渐变色系中随机选取一个颜色
    const color = randomPick(gradientColors);

    // 每个节段生成 3 到 8 个粒子
    const count = randomInt(3, 8);

    // 生成该节段的粒子
    const segmentParticles = spawnParticles(
      segment.x,
      segment.y,
      color,
      count,
      isMobile
    );

    // 将粒子追加到总数组
    allParticles.push(...segmentParticles);

    // 提前终止：如果粒子数已达上限，停止为更多节段生成粒子
    if (allParticles.length >= maxParticles) {
      break;
    }
  }

  // 最终裁剪到设备上限
  return limitParticles(allParticles, isMobile);
}